import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Timer, TrendingDown, CheckCircle2, Bell, RefreshCcw, Loader2, ArrowRight, Eye,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const BUCKETS = [
  { key: "fresh",    label: "Fresh",    color: "text-emerald-500 border-emerald-500/30 bg-emerald-500/5",  desc: "< 30 days old" },
  { key: "ripening", label: "Ripening", color: "text-sky-500 border-sky-500/30 bg-sky-500/5",              desc: "30–89 days" },
  { key: "stale",    label: "Stale",    color: "text-amber-500 border-amber-500/40 bg-amber-500/10",       desc: "90–179 days" },
  { key: "expired",  label: "Expired",  color: "text-red-500 border-red-500/40 bg-red-500/10",             desc: "180+ days" },
];

export default function KnowledgeDecay() {
  const { isAdmin } = useAuth();
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bucket, setBucket] = useState("stale");
  const [busyId, setBusyId] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewIdx, setPreviewIdx] = useState(0);

  const load = async (b = bucket) => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.get("/decay/summary"),
        api.get("/decay", { params: { bucket: b, limit: 200 } }),
      ]);
      setSummary(s.data);
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(bucket); /* eslint-disable-next-line */ }, [bucket]);

  const total = summary?.total || 0;
  const overdue = (summary?.buckets?.stale || 0) + (summary?.buckets?.expired || 0);

  const markReviewed = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/decay/${id}/mark-reviewed`, {});
      toast.success("Marked reviewed — freshness reset.");
      await load(bucket);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusyId(null); }
  };
  const pingOwner = async (id) => {
    setBusyId(id);
    try {
      const r = await api.post(`/decay/${id}/ping-owner`);
      toast.success(r.data.notified ? "Owner pinged." : "Nothing to ping.");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="decay-page">
      <header>
        <p className="text-[10px] uppercase tracking-[0.28em] text-amber-500">Knowledge decay</p>
        <h1 className="font-display text-3xl mt-1">Aging knowledge, at a glance</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Every entry has a freshness clock. Once it crosses 90 days without an edit or review, it's flagged as stale.
          Refresh it, mark it reviewed to reset the clock, or ping its owner to do the same.
        </p>
      </header>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="decay-summary">
          {BUCKETS.map((b) => (
            <button
              key={b.key}
              data-testid={`decay-bucket-${b.key}`}
              onClick={() => setBucket(b.key)}
              className={`text-left p-4 rounded-lg border transition-colors ${
                bucket === b.key ? b.color + " ring-1 ring-current/40" : "border-border/60 hover:bg-secondary/30"
              }`}
            >
              <p className="text-xs uppercase tracking-[0.18em] opacity-80">{b.label}</p>
              <p className="font-display text-2xl mt-1 tabular-nums">{summary.buckets[b.key] || 0}</p>
              <p className="text-[11px] mt-1 opacity-70">{b.desc}</p>
            </button>
          ))}
        </div>
      )}

      {summary && overdue > 0 && (
        <div className="border border-amber-500/40 bg-amber-500/5 rounded-lg p-4 flex items-center gap-3 text-sm text-amber-600 dark:text-amber-400"
             data-testid="decay-overdue-banner">
          <TrendingDown className="w-4 h-4" />
          <span className="flex-1">
            <strong>{overdue}</strong> of <strong>{total}</strong> entries need attention — they haven't been touched in 90+ days.
          </span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          Showing <span className="text-foreground font-medium">{rows.length}</span> {bucket} {rows.length === 1 ? "entry" : "entries"}
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <>
              <Button size="sm" variant="outline" data-testid="decay-preview-digest"
                      onClick={async () => {
                        setPreviewOpen(true); setPreviewLoading(true); setPreviewIdx(0);
                        try {
                          const r = await api.get("/decay/digest/preview");
                          setPreviewData(r.data);
                        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
                        finally { setPreviewLoading(false); }
                      }}>
                <Eye className="w-3.5 h-3.5 mr-1" /> Preview digest
              </Button>
              <Button size="sm" variant="outline" data-testid="decay-send-digest"
                      onClick={async () => {
                        if (!window.confirm("Send the weekly digest email to every owner of a stale/expired entry?")) return;
                        try {
                          const r = await api.post("/decay/digest/send");
                          toast.success(`Digest sent — ${r.data.emails_sent} email(s) to ${r.data.owners_notified} owner(s)`);
                        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
                      }}>
                <Bell className="w-3.5 h-3.5 mr-1" /> Send weekly digest
              </Button>
            </>
          )}
          <Button size="sm" variant="ghost" onClick={() => load(bucket)} data-testid="decay-refresh">
            <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      <div className="border border-border/60 rounded-lg divide-y divide-border/40" data-testid="decay-list">
        {loading && <div className="p-8 text-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="p-10 text-center text-muted-foreground" data-testid="decay-empty">
            Nothing in this bucket. Great — your team is keeping things fresh.
          </div>
        )}
        {!loading && rows.map((r) => (
          <div key={r.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`decay-row-${r.id}`}>
            <div className="flex-1 min-w-[280px]">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium truncate">{r.title}</p>
                <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                  BUCKETS.find((b) => b.key === r.bucket)?.color || ""
                }`}>{r.bucket} · {r.age_days}d</span>
              </div>
              {r.summary && <p className="text-xs text-muted-foreground truncate mt-0.5">{r.summary}</p>}
              <p className="text-[11px] text-muted-foreground mt-1">
                {r.category && <span className="mr-2">{r.category}</span>}
                {r.department && <span className="mr-2">· {r.department}</span>}
                {r.owner && <span>· owner: <span className="text-foreground/80">{r.owner.name || r.owner.email}</span></span>}
              </p>
            </div>
            <Link to={`/app/knowledge?open=${r.id}`}
                  className="text-xs text-sky-500 hover:underline"
                  data-testid={`decay-open-${r.id}`}>
              Open <ArrowRight className="w-3 h-3 inline" />
            </Link>
            {isAdmin && (
              <>
                <Button size="sm" variant="outline" onClick={() => markReviewed(r.id)}
                        disabled={busyId === r.id}
                        data-testid={`decay-review-${r.id}`}>
                  {busyId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                  Mark reviewed
                </Button>
                <Button size="sm" variant="ghost" onClick={() => pingOwner(r.id)}
                        disabled={busyId === r.id || !r.owner}
                        data-testid={`decay-ping-${r.id}`}>
                  <Bell className="w-3 h-3 mr-1" /> Ping owner
                </Button>
              </>
            )}
          </div>
        ))}
      </div>
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Weekly digest preview</DialogTitle>
            <DialogDescription>
              Exactly what each owner will receive when the Friday digest fires. No emails sent.
            </DialogDescription>
          </DialogHeader>
          {previewLoading && <div className="p-8 text-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Rendering…</div>}
          {!previewLoading && previewData?.empty && (
            <p className="text-sm text-muted-foreground py-8 text-center" data-testid="decay-preview-empty">
              {previewData.note}
            </p>
          )}
          {!previewLoading && !previewData?.empty && previewData?.previews && (
            <div className="space-y-3" data-testid="decay-preview-content">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {previewData.previews.map((p, i) => (
                  <button key={i} onClick={() => setPreviewIdx(i)}
                          className={`px-3 py-1 rounded-full border transition-colors ${
                            i === previewIdx
                              ? "border-sky-500/60 bg-sky-500/10 text-sky-500"
                              : "border-border/60 hover:bg-secondary/40"
                          }`}
                          data-testid={`decay-preview-tab-${i}`}>
                    {p.owner?.name || p.owner?.email || "unknown"} <span className="opacity-60">· {p.entry_count}</span>
                  </button>
                ))}
              </div>
              {previewData.previews[previewIdx] && (
                <div className="border border-border/60 rounded-lg overflow-hidden bg-[#0A0F1E]">
                  <div className="px-4 py-2 border-b border-border/60 bg-secondary/40 flex items-center justify-between text-xs">
                    <div>
                      <span className="text-muted-foreground">To:</span>{" "}
                      <span className="font-mono">{previewData.previews[previewIdx].owner?.email}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Subject:</span>{" "}
                      <span data-testid="decay-preview-subject">{previewData.previews[previewIdx].subject}</span>
                    </div>
                  </div>
                  <div
                    className="max-h-[420px] overflow-auto"
                    data-testid="decay-preview-html"
                    dangerouslySetInnerHTML={{ __html: previewData.previews[previewIdx].html }}
                  />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
